import { createAsyncThunk, createSlice, type ActionReducerMapBuilder, type AsyncThunk } from "@reduxjs/toolkit";
import {
  MembershipItem,
  type MembershipItem as MembershipItemType,
  type MembershipBag as MembershipBagType,
} from "@rtr/godmother";
import { type RootState } from "../../../util/store-types";
import $ from "clients/RawClient";
import { membershipTriggeredModalsCMSKeys } from "rtr-constants";

import { createAjaxAuthRetry } from "helpers/ajax-helpers";
import { getUserId } from "components/source/hoc/with-user-data";
import CEBagActions from "actions/ce-bag-actions";

const API_PATH = "/api/membership-items";

type initialStateType = {
  membershipItems: MembershipItemType[];
  smBag?: MembershipBagType;
  displayedModal?: string | null;
};
export const initialState: initialStateType = {
  membershipItems: [],
};

interface RefreshMembershipItemsResponse {
  membershipItems: MembershipItemType[];
  smBag: MembershipBagType;
}

export const refreshMembershipItemsCenter: AsyncThunk<
  RefreshMembershipItemsResponse,
  {
    onAlways?: () => void;
    onSuccess?: () => void;
    onFail?: () => void;
  },
  object
> = createAsyncThunk("membershipItems/refreshMembershipItemsCenter", async (args, { getState }) => {
  const { onFail, onAlways, onSuccess } = args || {};
  const state = getState() as RootState;
  const userId = getUserId(state?.userData);

  if (!userId) {
    throw new Error("User ID is required");
  }

  try {
    const membershipItems = (await $.ajax({
      url: `${API_PATH}?userId=${userId}`,
      type: "GET",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json; charset=utf-8",
      },
    })) as MembershipItem[];
    const smBag = (await $.get(`/membershipBags?includeProductDetail=true&includePricing=false`)) as MembershipBagType;
    if (typeof onSuccess === "function") {
      onSuccess();
    }

    return {
      membershipItems,
      smBag,
    };
  } catch (error) {
    console.error("Error in refreshMembershipItemsCenter:", error);
    if (typeof onFail === "function") {
      onFail();
    }
    throw error;
  } finally {
    if (typeof onAlways === "function") {
      onAlways();
    }
  }
});

export const fetchMembershipItems = createAsyncThunk("membershipItems/fetchMembershipItems", (userId: string) => {
  try {
    return $.ajax({
      type: "GET",
      url: `${API_PATH}?userId=${userId}`,
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json; charset=utf-8",
      },
    });
  } catch (error) {
    console.error("Error in fetchMembershipItems:", error);
    throw error;
  }
});

export const postMembershipKeptItems: AsyncThunk<
  void,
  {
    bookingId: string;
    onAlways?: () => void;
    onSuccess?: () => void;
    onFail?: () => void;
  },
  object
> = createAsyncThunk("membershipItems/postMembershipKeptItems", async (args, { dispatch }) => {
  const { bookingId, onSuccess, onFail, onAlways } = args;

  if (!bookingId) {
    throw new Error("Booking ID is required");
  }
  try {
    await $.ajax({
      url: `/membership_kept_items`,
      type: "POST",
      data: { bookingId },
    });

    dispatch(refreshMembershipItemsCenter({ onSuccess, onFail, onAlways }));
  } catch (error) {
    console.error("Error in postMembershipKeptItems:", error);
    if (typeof onFail === "function") {
      onFail();
    }
    if (typeof onAlways === "function") {
      onAlways();
    }
    throw error; // Re-throw the error to be caught by Redux Toolkit
  }
});

export const postMembershipSwapItems: AsyncThunk<
  void,
  {
    bookingId: string;
    onAlways?: () => void;
    onSuccess?: () => void;
    onFail?: () => void;
  },
  object
> = createAsyncThunk("membershipItems/postMembershipSwapItems", async (args, { dispatch }) => {
  const { bookingId, onAlways, onFail, onSuccess } = args;
  if (!bookingId) {
    throw new Error("Booking ID is required");
  }
  try {
    await $.ajax({
      url: `/item_return_promise_collections`,
      type: "POST",
      data: { bookingIds: [bookingId] },
    });

    dispatch(refreshMembershipItemsCenter({ onFail, onAlways, onSuccess }));
  } catch (error) {
    console.error("Error in postMembershipSwapItems:", error);
    if (typeof onFail === "function") {
      onFail();
    }
    if (typeof onAlways === "function") {
      onAlways();
    }
    throw error; // Re-throw the error to be caught by Redux Toolkit
  }
});

export const deleteBagItem: AsyncThunk<
  void,
  {
    bookingId: string;
    bagItemId: string;
    onAlways?: () => void;
    onSuccess?: () => void;
    onFail?: () => void;
  },
  object
> = createAsyncThunk("membershipItems/deleteBagItem", async (args, { dispatch }) => {
  const { bookingId, bagItemId, onFail, onAlways } = args;
  if (!bookingId) {
    throw new Error("Booking ID is required");
  }
  try {
    await createAjaxAuthRetry({
      url: "/bag/bagItem",
      type: "DELETE",
      data: {
        id: bagItemId,
        holdId: bookingId,
      },
    });

    dispatch(refreshMembershipItemsCenter({ onFail, onAlways }));
  } catch (error) {
    console.error("Error in deleteBagItem:", error);
    if (typeof onFail === "function") {
      onFail();
    }
    if (typeof onAlways === "function") {
      onAlways();
    }
    throw error; // Re-throw the error to be caught by Redux Toolkit
  }
});

export const buyItem: AsyncThunk<void, Record<string, string | number | undefined>, object> = createAsyncThunk(
  "membershipItems/buyItem",
  async ({ bookingId, orderId, sku }, { dispatch }) => {
    if (!bookingId) {
      throw new Error("Booking ID is required");
    } else if (!orderId) {
      throw new Error("Order ID is required");
    } else if (!sku) {
      throw new Error("SKU is required");
    }

    try {
      await dispatch(CEBagActions.kifah(sku, orderId, bookingId));
    } catch (error) {
      console.error("Error in buyItem:", error);
      throw error; // Re-throw the error to be caught by Redux Toolkit
    }
  }
);

const MembershipItemsSlice = createSlice({
  name: "membershipItems",
  initialState,
  reducers: {},
  extraReducers: (builder: ActionReducerMapBuilder<initialStateType>) => {
    builder.addCase(
      fetchMembershipItems.fulfilled,
      (state: initialStateType, action: { type: string; payload: MembershipItem[] }) => {
        state.membershipItems = action.payload;
      }
    );
    builder.addCase(fetchMembershipItems.rejected, (state: initialStateType) => {
      state.membershipItems = [];
    });
    builder.addCase(refreshMembershipItemsCenter.fulfilled, (state: initialStateType, action) => {
      state.membershipItems = action.payload.membershipItems;
      state.smBag = action.payload.smBag;
    });
    builder.addCase(refreshMembershipItemsCenter.rejected, (state: initialStateType) => {
      state.displayedModal = membershipTriggeredModalsCMSKeys.oops;
    });
    builder.addCase(buyItem.rejected, (state: initialStateType) => {
      state.displayedModal = membershipTriggeredModalsCMSKeys.oops;
    });
    builder.addCase(postMembershipKeptItems.rejected, (state: initialStateType) => {
      state.displayedModal = membershipTriggeredModalsCMSKeys.oops;
    });
    builder.addCase(postMembershipSwapItems.rejected, (state: initialStateType) => {
      state.displayedModal = membershipTriggeredModalsCMSKeys.oops;
    });
    builder.addCase(deleteBagItem.rejected, (state: initialStateType) => {
      state.displayedModal = membershipTriggeredModalsCMSKeys.oops;
    });
  },
});

/**
 * This value isn't used anywhere.
 * It's only defined because ReduxApp expects reducers to have a "types" property
 */
export const types = [
  "membershipItems/buyItem/fulfilled",
  "membershipItems/buyItem/rejected",
  "membershipItems/deleteBagItem/fulfilled",
  "membershipItems/deleteBagItem/rejected",
  "membershipItems/fetchMembershipItems/fulfilled",
  "membershipItems/fetchMembershipItems/rejected",
  "membershipItems/postMembershipKeptItems/fulfilled",
  "membershipItems/postMembershipKeptItems/rejected",
  "membershipItems/postMembershipSwapItems/fulfilled",
  "membershipItems/postMembershipSwapItems/rejected",
  "membershipItems/refreshMembershipItemsCenter/fulfilled",
  "membershipItems/refreshMembershipItemsCenter/rejected",
];

export default {
  types: types,
  fn: MembershipItemsSlice.reducer,
};
